/**
 * ChartTable Component
 * 
 * Displays a table of charts with edit and delete functionality
 * 
 * Features:
 * - View all charts in the system
 * - Edit chart details
 * - Delete charts
 * - Sortable columns
 * - Visual chart preview
 * - Tag display as badges
 */

import React, { useState } from 'react';

const ChartTable = ({ charts, onEdit, onDelete, isUpdating }) => {
  const [sortField, setSortField] = useState('created_at');
  const [sortOrder, setSortOrder] = useState('desc');
  const [deletingChartId, setDeletingChartId] = useState(null);

  /**
   * Handle column sort toggle
   */
  const handleSort = (field) => {
    if (sortField === field) {
      setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortOrder('desc');
    }
  };

  /**
   * Handle chart deletion with confirmation
   */
  const handleDelete = async (chartId, chartTitle) => {
    if (window.confirm(`Are you sure you want to delete "${chartTitle}"? This cannot be undone.`)) {
      setDeletingChartId(chartId);
      await onDelete(chartId);
      setDeletingChartId(null);
    }
  };

  /**
   * Format date for display
   */
  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  /**
   * Get sort indicator icon
   */
  const getSortIcon = (field) => {
    if (sortField !== field) return '⇅';
    return sortOrder === 'asc' ? '↑' : '↓';
  };

  /**
   * Truncate long text
   */
  const truncate = (text, maxLength = 100) => {
    if (!text) return '';
    return text.length > maxLength ? text.substring(0, maxLength) + '...' : text;
  };

  // If no charts, show empty state
  if (!charts || charts.length === 0) {
    return (
      <div className="empty-state">
        <p>No charts found. Create your first chart to get started!</p>
      </div>
    );
  }

  return (
    <div className="chart-table-container">
      <table className="chart-table">
        <thead>
          <tr>
            <th onClick={() => handleSort('id')} className="sortable">
              ID {getSortIcon('id')}
            </th>
            <th onClick={() => handleSort('title')} className="sortable">
              Title {getSortIcon('title')}
            </th>
            <th>Tooltip</th>
            <th>URL</th>
            <th>Tags</th>
            <th onClick={() => handleSort('created_at')} className="sortable">
              Created {getSortIcon('created_at')}
            </th>
            <th>Actions</th>
          </tr>
        </thead>
        <tbody>
          {charts.map((chart) => (
            <tr key={chart.id}>
              {/* Chart ID */}
              <td>{chart.id}</td>

              {/* Chart Title */}
              <td>
                <span className="chart-title">{chart.title}</span>
              </td>

              {/* Tooltip (truncated) */}
              <td>
                <span className="chart-tooltip" title={chart.tooltip}>
                  {truncate(chart.tooltip, 80)}
                </span>
              </td>

              {/* Chart URL */}
              <td>
                <code className="chart-url">{chart.url}</code>
              </td>

              {/* Tags */}
              <td>
                <div className="chart-tags">
                  {chart.tags && chart.tags.length > 0 ? (
                    chart.tags.map((tag, index) => (
                      <span key={index} className="tag-badge">
                        {tag}; 
                      </span>
                    ))
                  ) : (
                    <span className="no-tags">No tags</span>
                  )}
                </div>
              </td>

              {/* Created Date */}
              <td>{formatDate(chart.created_at)}</td>

              {/* Actions */}
              <td className="action-buttons">
                <button
                  className="edit-btn"
                  onClick={() => onEdit(chart)}
                  disabled={isUpdating}
                  title="Edit chart"
                >
                  ✏️ Edit
                </button>
                <button
                  className="delete-btn"
                  onClick={() => handleDelete(chart.id, chart.title)}
                  disabled={isUpdating || deletingChartId === chart.id}
                  title="Delete chart"
                >
                  {deletingChartId === chart.id ? '⏳' : '🗑️'} Delete
                </button>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default ChartTable;

